import { forwardRef } from "react";
import styled from "@emotion/styled";
import type { CSSObject } from "@emotion/react";
import { warningAccentBackgroundStyle, warningSurfaceTextStyle } from "@/components/ui/feedback/warningVisual";
import type { BadgeProps, BadgeSize, BadgeTone } from "./Badge.types";

interface BadgeOwnerState {
  size: BadgeSize;
  tone: BadgeTone;
}

/* Exact compiled output of the pre-migration Tailwind utilities. Note:
 * tailwind-merge drops the base `leading-[normal]` because every size class
 * carries a later font-size utility (fontSize conflicts with leading in
 * tw-merge). The effective line-height therefore comes from the size's
 * text-* class: md 1rem, lg 1.25rem, xs/sm inherited.
 * The warning tone keeps its inline-style treatment (warningVisual). */

const badgeBase: CSSObject = {
  display: "inline-flex",
  alignItems: "center",
  minWidth: "0px",
  borderRadius: "var(--bfrs-radius-badge)",
  fontWeight: 700,
  textTransform: "uppercase",
  letterSpacing: ".3px"
};

const badgeSizeStyles: Record<BadgeSize, CSSObject> = {
  xs: { paddingLeft: "9px", paddingRight: "9px", paddingTop: "3px", paddingBottom: "3px", fontSize: "10px" },
  sm: { paddingLeft: "9px", paddingRight: "9px", paddingTop: "3px", paddingBottom: "3px", fontSize: "11.5px" },
  md: { paddingLeft: ".625rem", paddingRight: ".625rem", paddingTop: ".25rem", paddingBottom: ".25rem", fontSize: ".75rem", lineHeight: "1rem" },
  lg: { paddingLeft: ".75rem", paddingRight: ".75rem", paddingTop: ".375rem", paddingBottom: ".375rem", fontSize: ".875rem", lineHeight: "1.25rem" }
};

const bg = (token: string): CSSObject => ({
  "--tw-bg-opacity": "1",
  backgroundColor: `hsl(var(--bfrs-color-${token}) / var(--tw-bg-opacity, 1))`
});
const text = (token: string): CSSObject => ({
  "--tw-text-opacity": "1",
  color: `hsl(var(--bfrs-color-${token}) / var(--tw-text-opacity, 1))`
});

const badgeToneStyles: Record<BadgeTone, CSSObject> = {
  neutral: { ...bg("surface-muted"), ...text("text-secondary") },
  primary: { ...bg("primary"), ...text("primary-contrast") },
  brand: { ...bg("primary-soft"), ...text("primary") },
  success: { ...bg("success-soft"), ...text("success-contrast") },
  warning: {},
  danger: { ...bg("danger-soft"), ...text("danger-contrast") },
  info: { ...bg("info-soft"), ...text("info-contrast") }
};

const badgeDotToneStyles: Record<BadgeTone, CSSObject> = {
  neutral: bg("text-muted"),
  primary: bg("primary"),
  brand: bg("primary"),
  success: bg("success"),
  warning: {},
  danger: bg("danger"),
  info: bg("info")
};

const BadgeRoot = styled("span", {
  label: "AgenticBadge-root",
  shouldForwardProp: (propName) => propName !== "ownerState"
})<{ ownerState: BadgeOwnerState }>(({ ownerState }) => [
  badgeBase,
  badgeSizeStyles[ownerState.size],
  badgeToneStyles[ownerState.tone]
]);

const BadgeDot = styled("span", {
  label: "AgenticBadge-dot",
  shouldForwardProp: (propName) => propName !== "ownerState"
})<{ ownerState: BadgeOwnerState }>(({ ownerState }) => [
  {
    marginRight: ".25rem",
    display: "inline-block",
    width: ".375rem",
    height: ".375rem",
    flexShrink: 0,
    borderRadius: "var(--bfrs-radius-pill)",
    verticalAlign: "middle"
  },
  badgeDotToneStyles[ownerState.tone]
]);

export const Badge = forwardRef<HTMLSpanElement, BadgeProps>(({ className, variant, tone, size = "sm", dot, count, maxCount = 99, children, style, ...props }, ref) => {
  const resolvedTone = (tone ?? (variant === "default" ? "neutral" : variant) ?? (typeof count === "number" ? "primary" : "neutral")) as BadgeTone;
  const content = typeof count === "number" ? (count > maxCount ? `${maxCount}+` : count) : children;
  const ownerState: BadgeOwnerState = { size, tone: resolvedTone };

  return (
    <BadgeRoot
      ref={ref}
      ownerState={ownerState}
      className={className}
      style={{ ...(resolvedTone === "warning" ? warningSurfaceTextStyle : undefined), ...style }}
      {...props}
    >
      {dot && <BadgeDot ownerState={ownerState} style={resolvedTone === "warning" ? warningAccentBackgroundStyle : undefined} aria-hidden="true" />}
      {content}
    </BadgeRoot>
  );
});

Badge.displayName = "Badge";
